import Image from 'next/image'
import { useCallback, useEffect, useRef, useState, type FormEventHandler } from 'react'
import { toast } from 'react-toastify'
import { useMirrorLoading } from 'shared-loading-indicator'
import type { MailingListLocaleResult } from '../data/remote/MailingListFragment'
import { api } from '../utilities/api'
import { Modal } from './Modal'
import styles from './NewsletterPopup.module.sass'
import { Input } from './Input'
import { useSite } from './contexts/SiteContextProvider'
import { useTranslate } from './contexts/TranslationsContextProvider'
import { useSelectedMailingList } from './contexts/MailingListContextProvider'
import { RichTextRenderer } from '@contember/react-client'
import { RichTextRendererWithElements } from './RichTextRendererWithElements'

type Props = {
    listId?: string
}

const SUBSCRIBED_COOKIE = 'nl_subscribed'
const SNOOZE_COOKIE = 'nl_snooze_until'
const MANUAL_TRIGGER_EVENT = 'newsletter-popup:open'

function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name: string, value: string, days: number) {
    if (typeof document === 'undefined') return
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${date.toUTCString()}`
}


function hasBlockedUtm(): boolean {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    const blocked = params.get('utm_campaign') === '2025-08-29-NL-HR-bodka-zlozvyky'
    return blocked
}

export function NewsletterPopup({ listId }: Props) {
    const translation = useTranslate()
    const { siteCode } = useSite()
    const selectedList: MailingListLocaleResult | undefined = useSelectedMailingList(listId)

    const [open, setOpen] = useState(false)
    const [email, setEmail] = useState('')
    const latestEmailRef = useRef(email)
    const form = useRef<HTMLFormElement>(null)

    const BrevoData = selectedList?.mailingList?.brevoData
    const mailingListId = selectedList?.id

    const mutation = api.subscribeOnMailingList.send.useMutation({
        onError: () => {
            toast('Něco se při odesílání pokazilo. Zkuste to znovu prosím později.', { type: 'error' })
        },
    })
    const isPending = mutation.isPending
    useMirrorLoading(isPending)

    const subscribe: FormEventHandler<HTMLFormElement> = useCallback(
        (event) => {
            event.preventDefault()
            if (!BrevoData || !mailingListId) {
                toast('Něco se při odesílání pokazilo. Zkuste to znovu prosím později.', { type: 'error' })
                return
            }
            mutation.mutate({ siteCode, mailingListId, brevoListId: BrevoData.listId, email })
        },
        [BrevoData, email, mailingListId, mutation, siteCode],
    )

    useEffect(() => {
        latestEmailRef.current = email
    }, [email])

    // dataLayer
    useEffect(() => {
        if (mutation.status === 'error' || mutation.status === 'success') {
            const isSuccess = mutation.data?.status === 'success'
            // success note + cookie to never show again
            if (isSuccess) {
                setCookie(SUBSCRIBED_COOKIE, '1', 365)
                setOpen(false)
                form.current?.reset()
                setEmail('')
            }
            if (typeof window !== 'undefined') {
                window.dataLayer = window.dataLayer || []
                
                if (isSuccess) {
                    // Track successful subscription
                    window.dataLayer.push({
                        event: "popup_newsletter_subscribe",
                        hit: {
                            event_type: "generic_event_content",
                            content_type: "popup",
                            content_id:
                                mutation.data?.contemberStatus && 'id' in (mutation.data.contemberStatus as unknown as { id?: string })
                                    ? (mutation.data.contemberStatus as unknown as { id?: string }).id ?? ''
                                    : '',
                            user_email: latestEmailRef.current,
                        },
                        _clear: true
                    })
                } else {
                    // Track error
                    window.dataLayer.push({
                        event: "popup_newsletter_error",
                        hit: {
                            event_type: "generic_event_content",
                            content_type: "popup"
                        },
                        _clear: true
                    })
                }
            }
        }
    }, [mutation.data, mutation.status])

    const snoozeDays = selectedList?.mailingList?.bannerSnoozeDays ?? 2
    const scrollPercent = selectedList?.mailingList?.bannerScrollPercent ?? 30

    // open logic: skip if utm blocked, subscribed cookie, or snoozed
    useEffect(() => {
        if (typeof window === 'undefined') return
        if (hasBlockedUtm()) return
        if (getCookie(SUBSCRIBED_COOKIE)) return
        const snoozeUntil = getCookie(SNOOZE_COOKIE)
        if (snoozeUntil && Date.now() < Number(snoozeUntil)) return

        const onScroll = () => {
            const scrolled = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
            if (scrolled >= scrollPercent) {
                setOpen(true)
                window.removeEventListener('scroll', onScroll)
            }
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [scrollPercent])


    // manual trigger enabled globally
    useEffect(() => {
        if (typeof window === 'undefined') return
        const handler = () => setOpen(true)
        window.addEventListener(MANUAL_TRIGGER_EVENT, handler)
        return () => window.removeEventListener(MANUAL_TRIGGER_EVENT, handler)
    }, [])

    // Track popup view when opened
    useEffect(() => {
        if (open && typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                event: "popup_newsletter_view",
                hit: {
                    event_type: "generic_event_content",
                    content_type: "popup"
                },
                _clear: true
            })
        }
    }, [open])

    const onClose = useCallback(() => {
        // snooze for requested time
        const until = Date.now() + snoozeDays * 24 * 60 * 60 * 1000
        setCookie(SNOOZE_COOKIE, String(until), snoozeDays)
        setOpen(false)
    }, [snoozeDays])

    if (!selectedList) return null

    return (
        <Modal isOpen={open} close={onClose} shouldCloseOnOverlayClick>
            <div className={styles.main} style={{ '--bannerAccentColor': selectedList.bannerAccentColor ?? '' } as React.CSSProperties}>
                <div className={styles.content}>
                    <div className={styles.header}>
                        <h3 className={styles.title}>
                            <RichTextRendererWithElements source={selectedList.bannerTitle ?? ''} />
                        </h3>

                        <button className={styles.close} aria-label="Close" type="button" onClick={onClose}>
                            ×
                        </button>
                    </div>
                    <div className={styles.row}>
                        <div className={styles.formContainer}>
                            {selectedList.bannerSubtitle && <div className={styles.description}>{selectedList.bannerSubtitle}</div>}
                            <form ref={form} onSubmit={subscribe} className={styles.form}>
                                <div className={styles.emailRow}>
                                    <Input type="email" required placeholder={translation('newsletterPopup.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} backgroundColor='orange' />
                                    <button type="submit" className={styles.sendButton} disabled={isPending}>
                                        {translation('newsletterPopup.button.label')}
                                    </button>
                                </div>
                                {mutation.data?.status !== undefined && (
                                    <div className={styles.note}>
                                        {mutation.data.status === 'success'
                                            ? translation('newsletter.subscription.success')
                                            : mutation.data.errorResponse && mutation.data.errorResponse.code === 'duplicate_parameter'
                                                ? translation('newsletter.subscription.duplicate_parameter')
                                                : translation('newsletter.subscription.error')}
                                    </div>
                                )}
                            </form>
                        </div>

                        {selectedList.bannerImage?.url && <div className={styles.illustration}>
                            <Image
                                src={selectedList.bannerImage?.url}
                                alt={selectedList.bannerImage?.alt ?? ''}
                                width={500}
                                height={300}
                                priority
                            />
                        </div>}
                        <div className={styles.almaLogo}>
                            <Image src="/images/alma-logo.svg" alt="Alma logo" width={140} height={70} priority />
                        </div>

                        {selectedList.bannerNote && <div className={styles.note}><RichTextRenderer source={selectedList.bannerNote ?? ''} /></div>}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

// Helper to programmatically open from a side banner: window.dispatchEvent(new Event('newsletter-popup:open'))
export function triggerNewsletterPopup() {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event(MANUAL_TRIGGER_EVENT))
}
